import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { GlobalState } from '@/redux/store';

import { getIsBot } from '../analytics';
import { getClientIpAddress } from '../browser';
import { getDeployment } from '../config';
import { fetchItemCarousels } from './itemCarousel.api';
import { shouldFetchCarousel } from './itemCarousel.selectors';
import { FetchItemCarouselsPayload } from './itemCarousel.types';

type FetchItemCarouselsParams = {
    itemId: number;
    shouldUseBackfillAuctioneerLots?: boolean;
    shouldUseMaximumLotsPerAuctioneer?: boolean;
    shouldUseSignalsBoost?: boolean;
    useNewSimilarItems?: boolean;
    useScoutCategories?: boolean;
};

export const loadItemCarousels = createAsyncThunk<
    FetchItemCarouselsPayload,
    FetchItemCarouselsParams,
    { state: GlobalState }
>(
    'la/domain/items/fetchItemCarousels',
    async (
        {
            itemId,
            shouldUseBackfillAuctioneerLots,
            shouldUseMaximumLotsPerAuctioneer,
            shouldUseSignalsBoost,
            useNewSimilarItems,
            useScoutCategories,
        },
        { getState }
    ) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const ipAddress = getClientIpAddress(state);
        const isBot = getIsBot(state);
        const { payload: carousels } = await fetchItemCarousels({
            authToken,
            deployment,
            ipAddress,
            isBot,
            itemId,
            useBackfillAuctioneerLots: shouldUseBackfillAuctioneerLots,
            useMaximumLotsPerAuctioneer: shouldUseMaximumLotsPerAuctioneer,
            useNewSimilarItems,
            useScoutCategories,
            useSignalsBoost: shouldUseSignalsBoost,
        });
        return { carousels, itemId };
    },
    {
        condition: (arg) => arg.itemId > 0,
    }
);

type FetchItemCarouselsIfNeededParams = {
    itemId: number;
    shouldUseBackfillAuctioneerLots?: boolean;
    shouldUseMaximumLotsPerAuctioneer?: boolean;
    shouldUseSignalsBoost?: boolean;
    useNewSimilarItems?: boolean;
    useScoutCategories?: boolean;
};

export const fetchItemCarouselsIfNeeded = createAsyncThunk<void, FetchItemCarouselsIfNeededParams>(
    'la/domain/items/fetchItemCarouselsIfNeeded',
    async (
        {
            itemId,
            shouldUseBackfillAuctioneerLots,
            shouldUseMaximumLotsPerAuctioneer,
            shouldUseSignalsBoost,
            useNewSimilarItems,
            useScoutCategories,
        },
        { dispatch, getState }
    ) => {
        const state = getState();
        if (shouldFetchCarousel(state, itemId)) {
            await dispatch(
                loadItemCarousels({
                    itemId,
                    shouldUseBackfillAuctioneerLots,
                    shouldUseMaximumLotsPerAuctioneer,
                    shouldUseSignalsBoost,
                    useNewSimilarItems,
                    useScoutCategories,
                })
            );
        }
    }
);
